import { useState, useMemo, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Filter, X, CalendarRange } from 'lucide-react';
import { useUnclaimedTravelTypes } from '@/hooks/useUnclaimed';
import { formatUnclaimedTravelTypeDisplay } from '@/components/unclaimed/unclaimedTravelType';

/**
 * Spec — Unclaimed Commission.docx (Report Page → Sorting & Filtering):
 *   "Sort and filter across all listed fields. For date fields, support
 *    date range filtering."
 *
 * Spec-listed filterable fields: Primary Traveler, Supplier, Travel Type,
 * Confirmation Number, Departure Date, Return Date.
 * AR-412: + Issue Date (when the invoice landed in the unclaimed pool).
 */
export interface UnclaimedFilters {
  primaryTraveler?: string;
  supplier?: string;
  travelType?: string;
  confirmationNumber?: string;
  departureDateFrom?: string;
  departureDateTo?: string;
  returnDateFrom?: string;
  returnDateTo?: string;
  issueDateFrom?: string;
  issueDateTo?: string;
}

interface UnclaimedFiltersBarProps {
  value: UnclaimedFilters;
  onChange: (next: UnclaimedFilters) => void;
}

function useFilterHelpers(value: UnclaimedFilters, onChange: (next: UnclaimedFilters) => void) {
  const update = (patch: Partial<UnclaimedFilters>) =>
    onChange({ ...value, ...patch });

  const clear = (key: keyof UnclaimedFilters) => {
    const next = { ...value };
    delete next[key];
    onChange(next);
  };

  const clearPair = (from: keyof UnclaimedFilters, to: keyof UnclaimedFilters) => {
    const next = { ...value };
    delete next[from];
    delete next[to];
    onChange(next);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (value.primaryTraveler) count++;
    if (value.supplier) count++;
    if (value.travelType) count++;
    if (value.confirmationNumber) count++;
    if (value.departureDateFrom || value.departureDateTo) count++;
    if (value.returnDateFrom || value.returnDateTo) count++;
    if (value.issueDateFrom || value.issueDateTo) count++;
    return count;
  }, [value]);

  return { update, clear, clearPair, activeFilterCount };
}

const BLANK_SENTINEL = '__blank__';

function TravelTypeSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { data: types = [] } = useUnclaimedTravelTypes();

  return (
    <Select
      value={value || 'all'}
      onValueChange={(v) => {
        if (v === 'all') onChange('');
        else if (v === BLANK_SENTINEL) onChange(BLANK_SENTINEL);
        else onChange(v);
      }}
    >
      <SelectTrigger aria-label="Travel type" className="h-11 text-base md:h-8 md:text-xs">
        <SelectValue placeholder="Travel Type" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Types</SelectItem>
        {types.map((t) => (
          <SelectItem key={t} value={t}>
            {formatUnclaimedTravelTypeDisplay(t)}
          </SelectItem>
        ))}
        <SelectItem value={BLANK_SENTINEL}>(Blank)</SelectItem>
      </SelectContent>
    </Select>
  );
}

/**
 * Filter icon button — render this wherever the trigger should appear
 * (e.g. in the page header next to action buttons).
 */
export function UnclaimedFiltersTrigger({
  value,
  onChange,
}: UnclaimedFiltersBarProps) {
  const [open, setOpen] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const { update, activeFilterCount } = useFilterHelpers(value, onChange);

  const depInverted =
    !!value.departureDateFrom &&
    !!value.departureDateTo &&
    value.departureDateFrom > value.departureDateTo;
  const retInverted =
    !!value.returnDateFrom &&
    !!value.returnDateTo &&
    value.returnDateFrom > value.returnDateTo;
  const issueInverted =
    !!value.issueDateFrom &&
    !!value.issueDateTo &&
    value.issueDateFrom > value.issueDateTo;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`relative inline-flex h-11 w-11 items-center justify-center rounded-md hover:bg-muted transition-colors md:h-auto md:w-auto md:p-1.5 ${
            activeFilterCount > 0 ? 'text-orange-600' : 'text-muted-foreground'
          }`}
          aria-label="Unclaimed filters"
        >
          <Filter className="h-4.5 w-4.5" />
          {activeFilterCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-orange-500 text-[9px] font-bold text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      {/* AR-210 (TC-16.3): named panel, Escape closes it from any field, and
          Reset all keeps focus in the panel after it unmounts. */}
      <PopoverContent
        className="w-[min(20rem,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto"
        align="end"
        aria-labelledby="unclaimed-filters-title"
        onKeyDown={(e) => {
          // Leave Escape to an open dropdown inside the panel (its listbox is
          // portalled, but React still bubbles its keydown here).
          if (
            e.key === 'Escape' &&
            !e.defaultPrevented &&
            !(e.target as HTMLElement).closest('[role="listbox"]')
          ) {
            e.preventDefault();
            setOpen(false);
          }
        }}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p id="unclaimed-filters-title" className="text-sm font-semibold">Filters</p>
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  onChange({});
                  window.requestAnimationFrame(() => firstFieldRef.current?.focus());
                }}
              >
                Reset all
              </Button>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Search</label>
            <Input
              placeholder="Primary Traveler..."
              className="h-11 text-base md:h-8 md:text-xs"
              aria-label="Primary traveler" ref={firstFieldRef} autoComplete="off" value={value.primaryTraveler ?? ''}
              onChange={(e) => update({ primaryTraveler: e.target.value })}
            />
            <Input
              placeholder="Supplier..."
              className="h-11 text-base md:h-8 md:text-xs"
              aria-label="Supplier" autoComplete="off" value={value.supplier ?? ''}
              onChange={(e) => update({ supplier: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-2">
              <TravelTypeSelect
                value={value.travelType ?? ''}
                onChange={(v) => update({ travelType: v || undefined })}
              />
              <Input
                placeholder="Confirm #..."
                className="h-11 text-base md:h-8 md:text-xs"
                aria-label="Confirmation number" autoComplete="off" value={value.confirmationNumber ?? ''}
                onChange={(e) => update({ confirmationNumber: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <CalendarRange className="h-3 w-3" />
              Date Ranges
            </label>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Departure</label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  className={`h-11 text-base md:h-8 md:text-xs ${depInverted ? 'border-rose-400' : ''}`}
                  aria-label="Departure date from" value={value.departureDateFrom ?? ''}
                  max={value.departureDateTo || undefined}
                  onChange={(e) => update({ departureDateFrom: e.target.value })}
                />
                <Input
                  type="date"
                  className={`h-11 text-base md:h-8 md:text-xs ${depInverted ? 'border-rose-400' : ''}`}
                  aria-label="Departure date to" value={value.departureDateTo ?? ''}
                  min={value.departureDateFrom || undefined}
                  onChange={(e) => update({ departureDateTo: e.target.value })}
                />
              </div>
              {depInverted && (
                <p role="alert" className="text-xs font-medium text-rose-600 dark:text-rose-400">
                  "From" must be on or before "To".
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Return</label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  className={`h-11 text-base md:h-8 md:text-xs ${retInverted ? 'border-rose-400' : ''}`}
                  aria-label="Return date from" value={value.returnDateFrom ?? ''}
                  max={value.returnDateTo || undefined}
                  onChange={(e) => update({ returnDateFrom: e.target.value })}
                />
                <Input
                  type="date"
                  className={`h-11 text-base md:h-8 md:text-xs ${retInverted ? 'border-rose-400' : ''}`}
                  aria-label="Return date to" value={value.returnDateTo ?? ''}
                  min={value.returnDateFrom || undefined}
                  onChange={(e) => update({ returnDateTo: e.target.value })}
                />
              </div>
              {retInverted && (
                <p role="alert" className="text-xs font-medium text-rose-600 dark:text-rose-400">
                  "From" must be on or before "To".
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Issue Date</label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  className={`h-11 text-base md:h-8 md:text-xs ${issueInverted ? 'border-rose-400' : ''}`}
                  aria-label="Issue date from" value={value.issueDateFrom ?? ''}
                  max={value.issueDateTo || undefined}
                  onChange={(e) => update({ issueDateFrom: e.target.value })}
                />
                <Input
                  type="date"
                  className={`h-11 text-base md:h-8 md:text-xs ${issueInverted ? 'border-rose-400' : ''}`}
                  aria-label="Issue date to" value={value.issueDateTo ?? ''}
                  min={value.issueDateFrom || undefined}
                  onChange={(e) => update({ issueDateTo: e.target.value })}
                />
              </div>
              {issueInverted && (
                <p role="alert" className="text-xs font-medium text-rose-600 dark:text-rose-400">
                  "From" must be on or before "To".
                </p>
              )}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Active filter pills — render below the header / above the table.
 */
export function UnclaimedFiltersBar({
  value,
  onChange,
}: UnclaimedFiltersBarProps) {
  const { clear, clearPair, activeFilterCount } = useFilterHelpers(value, onChange);

  if (activeFilterCount === 0) return null;

  // AR-412: pill clear buttons get a 44px hit area on a phone; the extra
  // row gap keeps wrapped rows' hit areas from overlapping.
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-4 md:gap-1.5">
      {value.primaryTraveler && (
        <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
          Traveler: {value.primaryTraveler}
          <button aria-label="Clear Traveler filter" onClick={() => clear('primaryTraveler')} className="-my-3.5 -mr-3 inline-flex h-11 w-11 items-center justify-center md:m-0 md:h-auto md:w-auto"><X className="h-3 w-3 md:h-2.5 md:w-2.5" /></button>
        </span>
      )}
      {value.supplier && (
        <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
          Supplier: {value.supplier}
          <button aria-label="Clear Supplier filter" onClick={() => clear('supplier')} className="-my-3.5 -mr-3 inline-flex h-11 w-11 items-center justify-center md:m-0 md:h-auto md:w-auto"><X className="h-3 w-3 md:h-2.5 md:w-2.5" /></button>
        </span>
      )}
      {value.travelType && (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
          Type:{' '}
          {value.travelType === BLANK_SENTINEL
            ? '(Blank)'
            : formatUnclaimedTravelTypeDisplay(value.travelType)}
          <button aria-label="Clear Travel Type filter" onClick={() => clear('travelType')} className="-my-3.5 -mr-3 inline-flex h-11 w-11 items-center justify-center md:m-0 md:h-auto md:w-auto"><X className="h-3 w-3 md:h-2.5 md:w-2.5" /></button>
        </span>
      )}
      {value.confirmationNumber && (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
          Confirm #: {value.confirmationNumber}
          <button aria-label="Clear Confirmation # filter" onClick={() => clear('confirmationNumber')} className="-my-3.5 -mr-3 inline-flex h-11 w-11 items-center justify-center md:m-0 md:h-auto md:w-auto"><X className="h-3 w-3 md:h-2.5 md:w-2.5" /></button>
        </span>
      )}
      {(value.departureDateFrom || value.departureDateTo) && (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
          Depart: {value.departureDateFrom || '…'} – {value.departureDateTo || '…'}
          <button aria-label="Clear Departure dates filter" onClick={() => clearPair('departureDateFrom', 'departureDateTo')} className="-my-3.5 -mr-3 inline-flex h-11 w-11 items-center justify-center md:m-0 md:h-auto md:w-auto"><X className="h-3 w-3 md:h-2.5 md:w-2.5" /></button>
        </span>
      )}
      {(value.returnDateFrom || value.returnDateTo) && (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
          Return: {value.returnDateFrom || '…'} – {value.returnDateTo || '…'}
          <button aria-label="Clear Return dates filter" onClick={() => clearPair('returnDateFrom', 'returnDateTo')} className="-my-3.5 -mr-3 inline-flex h-11 w-11 items-center justify-center md:m-0 md:h-auto md:w-auto"><X className="h-3 w-3 md:h-2.5 md:w-2.5" /></button>
        </span>
      )}
      {(value.issueDateFrom || value.issueDateTo) && (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
          Issued: {value.issueDateFrom || '…'} – {value.issueDateTo || '…'}
          <button aria-label="Clear Issue dates filter" onClick={() => clearPair('issueDateFrom', 'issueDateTo')} className="-my-3.5 -mr-3 inline-flex h-11 w-11 items-center justify-center md:m-0 md:h-auto md:w-auto"><X className="h-3 w-3 md:h-2.5 md:w-2.5" /></button>
        </span>
      )}
    </div>
  );
}
